import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function AdminResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError(t("admin.resetPassword.missingToken"));
      return;
    }
    if (newPassword.length < 8) {
      setError(t("admin.resetPassword.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("admin.resetPassword.mismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-reset-password-page" className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4">
      <div className="w-full max-w-sm bg-[#141414] border border-white/10 rounded-xl p-8">
        <h1 className="font-serif text-2xl text-white mb-1">
          Elite<span className="text-gold">The</span>Barber
        </h1>
        <p className="text-white/40 text-sm mb-8">{t("admin.resetPassword.title")}</p>

        {success ? (
          <p data-testid="admin-reset-password-success" className="text-white/80 text-sm">
            {t("admin.resetPassword.success")}
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="reset-new-password" className="text-white/70 text-sm">{t("admin.resetPassword.newPassword")}</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  data-testid="admin-reset-password-new-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white focus:border-gold rounded-none mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="reset-confirm-password" className="text-white/70 text-sm">{t("admin.resetPassword.confirmPassword")}</Label>
                <Input
                  id="reset-confirm-password"
                  type="password"
                  data-testid="admin-reset-password-confirm-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white focus:border-gold rounded-none mt-1.5"
                  required
                />
              </div>
            </div>

            {error && (
              <p data-testid="admin-reset-password-error" className="text-red-400 text-sm mt-4">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="admin-reset-password-submit-button"
              disabled={submitting}
              className="w-full mt-6 bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-60"
            >
              {submitting ? t("common.loading") : t("admin.resetPassword.submit")}
            </Button>
            <Link
              to="/admin/login"
              data-testid="admin-reset-password-login-link"
              className="block text-center mt-4 text-white/50 text-sm hover:text-white/80"
            >
              {t("admin.resetPassword.goToLogin")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
